import clsx from "clsx";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../context/SettingsContext";
import ColortAdjustmentWidget from "./ColortAdjustmentWidget";
import ContentAdjustmentWidget from "./ContentAdjustmentWidget";
import FlyoutAccessibilityGrid from "./FlyoutAccessibilityMenu";
import { Accordion } from "../Accordion";

interface AccessibilityAccordionCollectionProps {
  isFlyoutOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
}

const AccessibilityAccordionCollection: React.FC<
  AccessibilityAccordionCollectionProps
> = ({ isFlyoutOpen, setIsModalOpen }) => {
  const { t } = useTranslation();
  const { oversizeWidget } = useSettings();

  const [openAccordions, setOpenAccordions] = useState({
    content: true,
    color: true,
    orientation: true,
  });

  const toggleAccordion = (key: keyof typeof openAccordions) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const accordionTitleContainerClassName = clsx(
    oversizeWidget ? "font-semibold" : undefined,
    "flex items-start text-[20px] font-[500px] text-black"
  );

  // Memoized titles for Accordion components
  const contentAdjustmentTitle = useMemo(
    () => (
      <span className={accordionTitleContainerClassName}>
        {t("content_adjustmen")}
      </span>
    ),
    [accordionTitleContainerClassName, t]
  );

  const colorAdjustmentTitle = useMemo(
    () => (
      <span className={accordionTitleContainerClassName}>
        {t("color_adjustmen")}
      </span>
    ),
    [accordionTitleContainerClassName, t]
  );

  const orientationAdjustmentTitle = useMemo(
    () => (
      <span className={accordionTitleContainerClassName}>
        {t("orientation_adjustment")}
      </span>
    ),
    [accordionTitleContainerClassName, t]
  );

  // Memoized Accordion content components
  const memoizedContentAdjustment = useMemo(
    () => <ContentAdjustmentWidget setIsModalOpen={setIsModalOpen} />,
    [setIsModalOpen, isFlyoutOpen]
  );

  const memoizedColorAdjustment = useMemo(
    () => <ColortAdjustmentWidget setIsModalOpen={setIsModalOpen} />,
    [setIsModalOpen, isFlyoutOpen]
  );

  const memoizedOrientationAdjustment = useMemo(
    () => <FlyoutAccessibilityGrid setIsModalOpen={setIsModalOpen} />,
    [setIsModalOpen, isFlyoutOpen]
  );

  return (
    <div className="flex flex-col space-y-2">
      <Accordion
        variant="section"
        isOpen={openAccordions.content}
        onToggle={() => toggleAccordion("content")}
        title={contentAdjustmentTitle}
      >
        {memoizedContentAdjustment}
      </Accordion>
      <hr />
      <Accordion
        variant="section"
        isOpen={openAccordions.color}
        onToggle={() => toggleAccordion("color")}
        title={colorAdjustmentTitle}
      >
        {memoizedColorAdjustment}
      </Accordion>
      <hr />
      <Accordion
        variant="section"
        isOpen={openAccordions.orientation}
        onToggle={() => toggleAccordion("orientation")}
        title={orientationAdjustmentTitle}
      >
        {memoizedOrientationAdjustment}
      </Accordion>
      <hr />
    </div>
  );
};

export default AccessibilityAccordionCollection;
